import type { Point, RowRect } from './nestTarget'

/*
 * **Die Einfügelinie** beim Herausziehen einer Unteraufgabe (#204): wo in der Liste sie als
 * Aufgabe landet. Rein gerechnet, damit Linie und Loslassen dasselbe meinen.
 */

export interface LineSlot { rowId: string; before: boolean }

/**
 * Die Linie an der Zeile unter dem Zeiger: obere Hälfte davor, untere dahinter. Unter der
 * letzten Zeile hinter ihr, über der ersten davor — ist die letzte Aufgabe offen, gibt es unter
 * ihr keine Zeile mehr, auf die man zielen könnte. `null` zwischen zwei Zeilen (die Linie bleibt).
 */
export function lineAt(pointer: Point, rows: readonly RowRect[]): LineSlot | null {
  const row = rows.find(r => pointer.y >= r.top && pointer.y <= r.bottom)
  if (row) return { rowId: row.id, before: pointer.y < (row.top + row.bottom) / 2 }
  const first = rows[0]
  const last = rows[rows.length - 1]
  if (last && pointer.y > last.bottom) return { rowId: last.id, before: false }
  if (first && pointer.y < first.top) return { rowId: first.id, before: true }
  return null
}

interface Segment {
  section: { id: string; order: number } | null
  tasks: ReadonlyArray<{ id: string; order: number }>
}

/**
 * Abschnitt und `order` für eine Aufgabe an der Linie: die Mitte zwischen den Nachbarn. Oben
 * begrenzt der eigene Abschnittskopf, unten der nächste; Abschnitte und Aufgaben teilen
 * sich die Zahlen (`useTaskDragAndDrop`).
 */
export function orderAtLine(segments: readonly Segment[], slot: LineSlot): { sectionId: string | null; order: number } | null {
  const at = ordersAtLine(segments, slot, 1)
  return at && { sectionId: at.sectionId, order: at.orders[0]! }
}

/**
 * Wie {@link orderAtLine}, für `count` Aufgaben hintereinander (mehrere Unteraufgaben auf
 * einmal herausgezogen): gleiche Schritte zwischen den Nachbarn.
 */
export function ordersAtLine(segments: readonly Segment[], slot: LineSlot, count: number): { sectionId: string | null; orders: number[] } | null {
  const segIdx = segments.findIndex(seg => seg.tasks.some(t => t.id === slot.rowId))
  if (segIdx === -1) return null
  const seg = segments[segIdx]!
  const idx = seg.tasks.findIndex(t => t.id === slot.rowId)
  const prev = slot.before ? seg.tasks[idx - 1] : seg.tasks[idx]
  const next = slot.before ? seg.tasks[idx] : seg.tasks[idx + 1]
  const nextSection = segments[segIdx + 1]?.section ?? null

  const lower = prev?.order ?? seg.section?.order ?? next!.order - 1
  const upper = next?.order ?? nextSection?.order ?? lower + 2
  const step = (upper - lower) / (count + 1)
  return { sectionId: seg.section?.id ?? null, orders: Array.from({ length: count }, (_, i) => lower + step * (i + 1)) }
}

/**
 * **Die offene Checkliste als Ziel** (#204, Grill 11): Zieht man eine Aufgabe über die
 * Checkliste einer aufgeklappten Karte, steht sofort eine Linie zwischen den Unteraufgaben —
 * kein Verweilen. Über, unter und zwischen den Zeilen gilt die nächste Kante.
 *
 * @param rows die Unteraufgaben-Zeilen in ihrer Reihenfolge
 * @param list das Rechteck der ganzen Checkliste (mit offener Zeile)
 * @returns hinter welcher Zeile sie landet (`null` = ganz oben) und wo die Linie steht
 */
export function checklistSlotAt(
  pointer: Point,
  rows: readonly RowRect[],
  list: RowRect,
): { afterId: string | null; line: { top: number; left: number; width: number } } {
  if (rows.length === 0) return { afterId: null, line: { top: list.top, left: list.left, width: list.right - list.left } }
  let index = rows.findIndex(r => pointer.y < (r.top + r.bottom) / 2)
  if (index === -1) index = rows.length
  const above = rows[index - 1]
  const below = rows[index]
  const edge = below ? below.top - 1 : above!.bottom + 1
  const ref = below ?? above!
  return { afterId: above?.id ?? null, line: { top: edge, left: ref.left, width: ref.right - ref.left } }
}
